import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Typography from '@/v2/components/common/Typography'
import { useFindPatient } from '@/v2/hooks/patient/useFindPatient'
import { formatSearch } from '@/v2/utils/format'
import { getHighlightPhone, getHightlightText } from '@/v2/utils/highlight'

const SearchField = styled.div`
  min-width: 200px;

  && .MuiOutlinedInput-root {
    height: 34px;
  }

  &&& .MuiInputBase-input {
    padding: 0;
  }

  && .MuiAutocomplete-endAdornment {
    display: none;
  }
`

const OptionItem = React.memo(({ option, searchTerm }) => (
  <>
    <Typography variant="h4" style={{ textTransform: 'capitalize' }}>
      {option.fullName
        ? getHightlightText(option.fullName, searchTerm)
        : option.firstName
        ? `${getHightlightText(
            option.firstName,
            searchTerm
          )} ${getHightlightText(option.lastName, searchTerm)}`
        : 'No Name'}
    </Typography>
    <Typography variant="h5">
      {getHighlightPhone(option?.phoneNumber, searchTerm)}
    </Typography>
    <Typography variant="h5">
      {getHightlightText(option?.email, searchTerm)}
    </Typography>
  </>
))

const PatientSearch = ({
  showSearchIcon,
  autoFocus,
  error,
  onChange,
  value,
}) => {
  const theme = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const { patients = [], isLoading, isError } = useFindPatient(searchTerm)
  const isMounted = useRef(true)
  const debounceTimer = useRef(null)

  useEffect(() => {
    return () => {
      isMounted.current = false
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  const handleChangeSearch = useCallback((e, term) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      if (isMounted.current) {
        setSearchTerm(formatSearch(term))
      }
    }, 300)
  }, [])

  const handleChange = useCallback(
    (e, patient) => {
      onChange(patient)
    },
    [onChange]
  )

  const getOptionLabel = useCallback(option => {
    if (typeof option === 'string') return option
    return option
      ? option.fullName ||
          (option.firstName
            ? `${option.firstName} ${option.lastName}`
            : 'No Name')
      : 'No Name'
  }, [])

  const isOptionEqualToValue = useCallback(
    (option, value) => option?.id === value?.id,
    []
  )

  const renderOption = useCallback(
    (props, option) => (
      <li {...props} key={option.id} style={{ display: 'block' }}>
        <OptionItem option={option} searchTerm={searchTerm} />
      </li>
    ),
    [searchTerm]
  )

  const renderInput = useCallback(
    params => (
      <TextField
        autoFocus={autoFocus}
        placeholder="Search for patient"
        {...params}
        sx={{
          ...params.sx,
          '& .MuiInputBase-root': { paddingRight: '9px !important' },
        }}
        InputProps={
          showSearchIcon
            ? {
                ...params.InputProps,
                endAdornment: <SearchIcon sx={{ marginTop: '-3px' }} />,
              }
            : params.InputProps
        }
      />
    ),
    [autoFocus, showSearchIcon]
  )

  const autocompleteProps = useMemo(
    () => ({
      fullWidth: true,
      autoComplete: true,
      includeInputInList: true,
      filterSelectedOptions: true,
      filterOptions: x => x,
      options: patients,
      value: value,
      loading: searchTerm ? isLoading : false,
      renderInput,
      onInputChange: handleChangeSearch,
      getOptionLabel,
      isOptionEqualToValue,
      noOptionsText: 'There are no results',
      onChange: handleChange,
      renderOption,
      ListboxProps: {
        style: { maxHeight: '300px' },
      },
    }),
    [
      patients,
      value,
      searchTerm,
      isLoading,
      renderInput,
      handleChangeSearch,
      getOptionLabel,
      isOptionEqualToValue,
      handleChange,
      renderOption,
    ]
  )

  return (
    <SearchField>
      <Autocomplete {...autocompleteProps} />
      {(error || isError) && (
        <Typography
          fontSize="10px"
          color={theme.palette.error.main}
          sx={{ mt: '4px !important' }}
        >
          {error || 'Unable to search patients. Please try again.'}
        </Typography>
      )}
    </SearchField>
  )
}

export default React.memo(PatientSearch)
